import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';

import { loadBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import {
  getBulkQueuePollIntervalMs,
} from '@/lib/bulkQueuePoll';
import { isBulkTargetPendingHydration } from '@/lib/bulkProcessQueueSync';
import { createApplicationListSync } from '@/lib/applicationListSync';
import { syncBulkQueueProgress } from '@/lib/syncBulkQueueProgress';
import type { Application } from '@/types';

type SyncBannerFn = (
  companyId: string,
  processingIds: Set<string>,
  stoppingIds: Set<string>,
  applications?: Application[],
) => void;

/**
 * Adaptive polling loop for bulk queue sync (replaces fixed setInterval).
 */
export function useAdaptiveBulkQueuePoll(
  active: boolean,
  companyId: string | undefined,
  queryClient: QueryClient,
  syncBanner: SyncBannerFn,
  options?: { targetIds?: readonly string[] },
): void {
  const syncBannerRef = useRef(syncBanner);
  syncBannerRef.current = syncBanner;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!active || !companyId) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleNext = () => {
      const loaded = loadBulkProcessQueue(companyId);
      const targetIds = optionsRef.current?.targetIds?.length
        ? optionsRef.current.targetIds
        : loaded.targetIds;
      const apps = createApplicationListSync(queryClient).getFlattenedListApplications(
        companyId,
        false,
      );

      const pendingIds = targetIds.filter((id) => {
        const row = apps.find((a) => a.id === id);
        return isBulkTargetPendingHydration(row);
      });

      const delayMs = getBulkQueuePollIntervalMs(apps, pendingIds);
      timeoutId = setTimeout(() => {
        void tick();
      }, delayMs);
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        await syncBulkQueueProgress(queryClient, companyId, syncBannerRef.current, {
          targetIds: optionsRef.current?.targetIds,
        });
      } catch {
        // Keep polling — transient bulk-status / list errors must not freeze the banner loop.
      }
      if (cancelled) return;
      scheduleNext();
    };

    void tick();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [active, companyId, queryClient]);
}
